import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { CheckCircle2, Megaphone, Users } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { ShareEvent } from "@/components/site/share-event";
import { AddToCalendar } from "@/components/site/add-to-calendar";
import type { CalendarInvite } from "@/lib/calendar-invite";
import { getStoredAthleteRef } from "@/lib/athlete-ref";
import { selfSignupFromClaim } from "@/lib/volunteer-selfsignup.functions";
import { track } from "@/lib/analytics";
import { reportHandledSync } from "@/lib/observability";


export function PostClaimCta({
  eventId,
  slug,
  title,
  shareEnabled,
  shareChannels,
  shareMessage,
  email,
  fullName,
  calendarInvite,
}: {
  eventId?: string | null;
  slug: string;
  title: string;
  shareEnabled?: boolean;
  shareChannels?: string[] | null;
  shareMessage?: string | null;
  email?: string | null;
  fullName?: string | null;
  calendarInvite?: CalendarInvite | null;
}) {
  const becomeVolunteerFn = useServerFn(selfSignupFromClaim);
  const [joining, setJoining] = useState(false);
  const [becameVolunteer, setBecameVolunteer] = useState(false);

  async function becomeVolunteer() {
    if (!email) {
      toast.error("We need the email you claimed with to sign you up.");
      return;
    }
    setJoining(true);
    try {
      const result = await becomeVolunteerFn({
        data: { email, full_name: fullName ?? null, volunteer_track: "both", athlete_ref: getStoredAthleteRef() },
      });
      track("volunteer_selfsignup", { slug, metadata: { track: "both" } });
      setBecameVolunteer(true);
      toast.success(
        result.alreadyVolunteer
          ? "You are already on the roster. Sign in any time."
          : "You are on the roster. Check your email to set your password.",
      );
    } catch (error) {
      reportHandledSync(error, { feature: "post-claim-cta", action: "becomeVolunteer" });
      toast.error(error instanceof Error ? error.message : "Could not sign you up.");
    } finally {
      setJoining(false);
    }
  }







  return (
    <div className="mt-5 border border-cyan/40 bg-cyan/5 p-5" style={{ borderRadius: 4 }}>
      <p className="flex items-center gap-2 text-[1.0625rem] font-bold text-foreground">
        <CheckCircle2 className="h-5 w-5 shrink-0 text-cyan" />
        Your spot is locked in
      </p>
      <p className="t-body mt-1 text-[0.9375rem] text-muted-foreground">
        Check your email for the details. Two quick things that help us more than you think.
      </p>

      {calendarInvite ? (
        <div className="mt-4">
          <AddToCalendar invite={calendarInvite} />
        </div>
      ) : null}

      {shareEnabled !== false ? (
        <div className="mt-5 border-t border-cyan/20 pt-4">
          <p className="flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-foreground">
            <Megaphone className="h-4 w-4 shrink-0 text-cyan" />
            1. Share it with one person
          </p>
          <p className="t-body mt-1 text-[0.9375rem] text-muted-foreground">
            One post or one text is all we ask. Most of our crew found us because a friend passed
            the link along.
          </p>
          <div className="mt-3">
            <ShareEvent
              inline
              title={title}
              slug={slug}
              channels={shareChannels}
              message={shareMessage}
            />
          </div>
        </div>
      ) : null}

      <div className="mt-5 border-t border-cyan/20 pt-4">
        <p className="flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-foreground">
          <Users className="h-4 w-4 shrink-0 text-cyan" />
          2. Come back for more than one day
        </p>
        <p className="t-body mt-1 text-[0.9375rem] text-muted-foreground">
          Join the volunteer roster and we will send you both kinds of opportunities: events with
          the on-site crew and easy help from home, about an hour a week. No application to fill
          out, we already have your details from this claim.
        </p>
        {becameVolunteer ? (
          <p className="t-body mt-3 text-[0.9375rem] text-foreground">
            You are on the roster. Check your email for the link to set your password, plus a list
            of events you can claim next.
          </p>
        ) : (
          <div className="mt-3">
            <Button
              className="w-full uppercase sm:w-auto"
              disabled={joining}
              onClick={() => becomeVolunteer()}
            >
              {joining ? "Signing you up..." : "Join the volunteer roster"}
            </Button>
          </div>
        )}
      </div>


    </div>
  );
}
